import React, {useEffect} from 'react';
import { ICart, IQuoteRequestItem } from '../../Api/Models/Cart';
import CurrencyFormat from '../Helpers/CurrencyFormat';
import {
    Button,
    Tooltip,
} from '@fluentui/react-components';
import { useNavigate } from 'react-router-dom';
import {
    useGetStorefrontQuery,
    useStartCheckoutMutation
} from '../../Api/Api';
import {NewFilled, ShoppingBagAddFilled, ShareRegular, PaymentFilled, HandshakeRegular, CommentRegular} from "@fluentui/react-icons";

import {useGetPreparedCartQuery, useStartPreparedCartCheckoutMutation} from "../../Api/PreparedCartEndpoints";
import ShareCartDialog from './ShareCartDialog';
import QuoteRequestDrawer from './QuoteRequestDrawer';
import {useStartNewCart} from "./useStartNewCart";
import FullPageSpinner from '../Spinner/FullPageSpinner';
import CustomerCommentDialog from "./CustomerCommentDialog";
import { parseCartPageConfig } from '../Helpers/cartPageConfig';
import { elementStyleFrom, importantStyleCss } from '../Helpers/searchPageConfig';
import { hoverPartFor } from '../Helpers/stylesConfig';
import { useLocalization } from '../Helpers/useLocalization';

interface ICartSummaryProps {
    cart?: ICart;
    preparedCartId?: string;
    error?: string;
}

const CartSummary: React.FC<ICartSummaryProps> = (props) => {
    const subtotal = (props.cart?.products.reduce((acc, product) => acc + (product.price || 0) * product.quantity, 0) ?? 0) +
        (props.cart?.bundles.reduce((acc, bundle) => acc + (bundle.price || 0) * bundle.quantity, 0) ?? 0);
    const itemCount = (props.cart?.products.reduce((acc, product) => acc + product.quantity, 0) ?? 0) +
        (props.cart?.bundles.reduce((acc, bundle) => acc + bundle.quantity, 0) ?? 0);

    const [startingCheckout, setStartingCheckout] = React.useState<boolean>(false)
    const [error, setError] = React.useState<string | undefined>();
    const navigate = useNavigate();
    const L = useLocalization();
    const [startCheckout] = useStartCheckoutMutation();
    const [startCheckoutPreparedCart] = useStartPreparedCartCheckoutMutation();
    const { startNewCart, canStartNewCart } = useStartNewCart();
    const settings = useGetStorefrontQuery();
    // Merchant cart-page styling for the summary box's own elements.
    const cartConfig = React.useMemo(() => parseCartPageConfig(settings.data?.cartPageConfig), [settings.data?.cartPageConfig]);
    const summaryHeadingStyle = React.useMemo(() => elementStyleFrom(cartConfig.summaryHeading, 'heading'), [cartConfig.summaryHeading]);
    const checkoutBtnCss = React.useMemo(() => importantStyleCss(elementStyleFrom(cartConfig.checkoutButton)), [cartConfig.checkoutButton]);
    const secondaryBtnCss = React.useMemo(() => importantStyleCss(elementStyleFrom(cartConfig.secondaryButton)), [cartConfig.secondaryButton]);
    // Hover comes from the `hover` part of the button style each button references (there are no
    // local cart hover config keys). Undefined when nothing is referenced => no extra rule.
    const checkoutBtnHoverCss = React.useMemo(() => { const s = elementStyleFrom(hoverPartFor(cartConfig.checkoutButton), 'hover'); return Object.keys(s).length ? importantStyleCss(s) : undefined; }, [cartConfig.checkoutButton]);
    const secondaryBtnHoverCss = React.useMemo(() => { const s = elementStyleFrom(hoverPartFor(cartConfig.secondaryButton), 'hover'); return Object.keys(s).length ? importantStyleCss(s) : undefined; }, [cartConfig.secondaryButton]);

    const preparedCart = useGetPreparedCartQuery({cartId: props.preparedCartId ?? ''}, {skip: !props.preparedCartId});
    const [quoteDrawerOpen, setQuoteDrawerOpen] = React.useState(false);
    const [shareDialogOpen, setShareDialogOpen] = React.useState(false);
    const [commentDialogOpen, setCommentDialogOpen] = React.useState(false);

    const [canShare, setCanShare] = React.useState(false)

    useEffect(() => {
        if(props.preparedCartId && preparedCart.data){
            setCanShare(preparedCart.data.canShare ?? false);
        }
        else if(!props.preparedCartId && settings.data){
            setCanShare(settings.data.allowCartSharing ?? false);
        }
        
    }, [props.preparedCartId, preparedCart.data, settings.data]);

    const checkout = async () => {
        setStartingCheckout(true);
        const result = props.preparedCartId ? await startCheckoutPreparedCart({cartId: props.preparedCartId}) : await startCheckout();
        if (result.error) {
            setStartingCheckout(false);
            setError(`${L('localization_cart_checkout_error')} \r\n ${result.error}`);
            return;
        }
        // Server refused checkout because the cart holds quote-only items. The inline notice (keyed on
        // hasQuoteRequiredItem) normally already covers this; guard here so a stale cart flag can't fall
        // through to the generic error path.
        if (result.data?.requiresQuote) {
            setStartingCheckout(false);
            setError(undefined);
            return;
        }
        // Server re-conformed one or more cart bundles because their definition changed (new required
        // items and/or removed items). The cart has been updated (and the cache invalidated so the new
        // line items show); ask the shopper to review and check out again.
        if (result.data?.bundleChanged) {
            setStartingCheckout(false);
            const names = result.data.changedBundles ?? [];
            setError(names.map(n => L('localization_cart_bundle_changed').replace('{bundleName}', n)).join('\n'));
            return;
        }
        // Server re-resolved a bundle's saved-search item to a differently-priced product. The cart bundle
        // selection has been updated (and priced); ask the shopper to review the new price and check out again.
        if (result.data?.bundlePriceChanged) {
            setStartingCheckout(false);
            const names = result.data.priceChangedBundles ?? [];
            setError(names.map(n => L('localization_cart_bundle_price_changed').replace('{bundleName}', n)).join('\n'));
            return;
        }
        if (result.data?.error || !result.data?.orderId) {
            //TODO: Handle error
            setStartingCheckout(false);
            setError(`${L('localization_cart_checkout_error')} \r\n ${result.data.error}`);
            return;
        }
        navigate(`/checkoutPrerequisites/${result.data.orderId}`);
    }
    
    const onNewCart = async () => {
        await startNewCart();
        navigate('/home');
    }

    // "Hide Price On Storefront" items can sit in the cart but can never be purchased directly — the shopper
    // must request a quote. When any are present we disable Checkout and surface an inline notice (the server
    // enforces the same block on /cart/checkout, so a stale client can't slip one through).
    const quoteRequiredItems = React.useMemo(
        () => (props.cart?.products ?? []).filter(p => p.hidePrice),
        [props.cart?.products]
    );
    const hasQuoteRequiredItem = quoteRequiredItems.length > 0;
    const salesRepEmail = settings.data?.defaultSalesRep;

    const quoteItems: IQuoteRequestItem[] = React.useMemo(() => [
        ...(props.cart?.products.map(p => ({ id: p.productId, quantity: p.quantity, price: p.price, itemType: 'product' as const })) ?? []),
        ...(props.cart?.bundles.map(b => ({ id: b.bundleId, quantity: b.quantity, price: b.price, itemType: 'bundle' as const })) ?? []),
    ], [props.cart?.products, props.cart?.bundles]);

    return (
        <>
            {startingCheckout && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    background: 'rgba(255,255,255,0.85)',
                    zIndex: 9999,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}>
                    <FullPageSpinner message={L('localization_cart_preparing_order')} />
                </div>
            )}
            <div>
                {/* The resting declarations also cover :hover (so a configured button doesn't flip
                    back to the theme color on hover); a referenced style's `hover` part is then
                    layered on top by the rule that follows it. */}
                {(checkoutBtnCss || secondaryBtnCss || checkoutBtnHoverCss || secondaryBtnHoverCss) && <style>{`${checkoutBtnCss ? `.cart-checkout-btn, .cart-checkout-btn:hover { ${checkoutBtnCss} }` : ''}${secondaryBtnCss ? `.cart-summary-btn, .cart-summary-btn:hover { ${secondaryBtnCss} }` : ''}${checkoutBtnHoverCss ? `.cart-checkout-btn:hover { ${checkoutBtnHoverCss} }` : ''}${secondaryBtnHoverCss ? `.cart-summary-btn:hover { ${secondaryBtnHoverCss} }` : ''}`}</style>}
                <h2 style={summaryHeadingStyle}>{L('localization_cart_summary_title')}</h2>
                {itemCount > 0 && <p style={{fontSize: '120%'}}>
                    <span style={{fontWeight: 'bold'}}>{L('localization_cart_items_label')}</span>{' '}
                    {itemCount}
                </p>}
                {subtotal > 0 && <p style={{fontSize: '120%'}} data-testid='cart-subtotal' data-value={subtotal}>
                    <span style={{fontWeight: 'bold'}}>{L('localization_cart_subtotal_label')}</span>{' '}
                    <CurrencyFormat value={subtotal}/>
                </p>}
                <Button className='cart-checkout-btn' appearance='primary' style={{width: '100%'}} icon={<PaymentFilled/>} disabled={startingCheckout || hasQuoteRequiredItem}
                        onClick={checkout}>{L('localization_cart_checkout_button')}</Button>
                {hasQuoteRequiredItem && (
                    <div className='cart-quote-required-notice' role='alert' style={{marginTop: '1em', padding: '0.75em 1em', border: '1px solid #d1a800', borderRadius: 6, background: 'rgba(209, 168, 0, 0.08)'}}>
                        <div style={{fontWeight: 600, marginBottom: '0.4em'}}>{L('localization_quote_required_notice')}</div>
                        <ul style={{margin: '0 0 0.5em', paddingLeft: '1.2em'}}>
                            {quoteRequiredItems.map(p => (
                                <li key={p.productId}>{p.name ?? p.productId}</li>
                            ))}
                        </ul>
                        <div>
                            {L('localization_quote_contact_rep')}
                            {salesRepEmail && (
                                salesRepEmail.includes('@')
                                    ? <> <a href={`mailto:${salesRepEmail}`}>{salesRepEmail}</a></>
                                    : <> {salesRepEmail}</>
                            )}
                        </div>
                    </div>
                )}
                <div style={{marginTop: '2em'}} />
                {settings.data?.allowRequestQuote && settings.data?.defaultSalesRep && !props.preparedCartId &&
                    <Button className='cart-summary-btn' appearance='secondary' icon={<HandshakeRegular/>} style={{width: '100%'}} onClick={() => setQuoteDrawerOpen(true)}>{L('localization_cart_request_quote')}</Button>}
                <div>
                {preparedCart.data?.canAddAdditionalItems &&
                    <Button className='cart-summary-btn' appearance='secondary' style={{width: '100%'}} onClick={() => navigate('/home')} icon={<ShoppingBagAddFilled/>}>{L('localization_cart_add_more_products')}</Button>
                }
                {preparedCart.data?.allowCustomerContact &&
                    <Button className='cart-summary-btn' appearance='secondary' style={{width: '100%'}} onClick={() => setCommentDialogOpen(true)} icon={<CommentRegular/>}>{L('localization_cart_comment_button')}</Button>
                }
                {canShare && (
                    // A cart holding a quote-required (hide-price) item can't be checked out, so sharing it
                    // would just hand the recipient an un-purchasable cart. Block share the same way as checkout.
                    hasQuoteRequiredItem ? (
                        <Tooltip relationship='label' content={L('localization_cart_share_blocked_quote', 'One or more items require a quote and cannot be shared. Request a quote instead.')}>
                            <Button className='cart-summary-btn' appearance='secondary' style={{width: '100%'}} disabledFocusable icon={<ShareRegular/>}>{L('localization_cart_share_cart')}</Button>
                        </Tooltip>
                    ) : (
                        <Button className='cart-summary-btn' appearance='secondary' style={{width: '100%'}} onClick={() => setShareDialogOpen(true)} icon={<ShareRegular/>}>{L('localization_cart_share_cart')}</Button>
                    )
                )}
                {preparedCart.data && canStartNewCart &&
                    <Button className='cart-summary-btn' appearance='transparent' style={{width: '100%', marginTop: '2em'}} onClick={onNewCart} icon={<NewFilled/>}>{L('localization_cart_start_new_cart')}</Button>
                }
                </div>
                {error && <p style={{color: 'red', whiteSpace: 'pre-line'}}>{error}</p>}
                {props.error && <p style={{color: 'red'}}>{props.error}</p>}
            </div>

            <QuoteRequestDrawer
                open={quoteDrawerOpen}
                onOpenChange={setQuoteDrawerOpen}
                items={quoteItems}
            />
            {props.cart && (
                <ShareCartDialog
                    preparedCartId={props.preparedCartId}
                    cart={props.cart}
                    open={shareDialogOpen}
                    onClose={() => setShareDialogOpen(false)}
                />
            )}
            {preparedCart.data?.allowCustomerContact && props.preparedCartId &&
                <CustomerCommentDialog 
                    cartId={props.preparedCartId} 
                    open={commentDialogOpen} 
                    onClose={() => setCommentDialogOpen(false)} 
                />
            }
        </>
    );
}

export default CartSummary;

